import { useDispatch } from 'react-redux';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import * as UUID from 'uuid';
import { ErrorType } from '../../global/types/error';
import { mapError } from '../../utils/map-error';
import { NotificationType } from './notification-types';
import { createNotification, removeNotification } from './notifications-redux';

export const useNotifications = () => {
  const dispatch = useDispatch();

  const remove = useCallback(
    (id: string) => {
      dispatch(removeNotification({ id }));
    },
    [dispatch],
  );

  const create = useCallback(
    (options: NotificationType) => {
      const id = UUID.v4();
      dispatch(createNotification({ ...options, id }));

      if (options.durationMs && options.durationMs !== 'Infinity') {
        setTimeout(() => {
          remove(id);
        }, options.durationMs);
      }

      if (
        options.durationMs === undefined
        && options.durationMs !== 'Infinity'
        && options.severity !== 'error'
      ) {
        setTimeout(() => {
          remove(id);
        }, 6000);
      }
    },
    [dispatch, remove],
  );

  const success = useCallback(
    (options: Omit<NotificationType, 'severity'>) => {
      create({
        ...options,
        severity: 'success',
      });
    },
    [create],
  );

  const error = useCallback(
    (options: Omit<NotificationType, 'severity'>) => {
      create({
        ...options,
        severity: 'error',
      });
    },
    [create],
  );

  const info = useCallback(
    (options: Omit<NotificationType, 'severity'>) => {
      create({
        ...options,
        severity: 'info',
      });
    },
    [create],
  );

  return useMemo(
    () => ({
      create,
      remove,

      success,
      error,
      info,
    }),
    [info, create, error, remove, success],
  );
};

export const useCreateNotification = (
  condition: boolean,
  options: NotificationType,
) => {
  const notifications = useNotifications();
  const $options = useRef(options);

  useEffect(() => {
    $options.current = options;
  });

  useEffect(() => {
    if (condition) {
      notifications.create($options.current);
    }
  }, [condition, notifications]);
};

export const useCreateNotificationError = (
  condition: boolean,
  error: ErrorType | null,
) => {
  const notifications = useNotifications();

  useEffect(() => {
    if (error) {
      const { message } = mapError(error);

      if (condition) {
        notifications.create({
          severity: 'error',
          description: message,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition, error?.message, error?.response?.status, notifications]);
};
